/**
 * FollowCreatorButton.jsx
 *
 * Dos botones separados que aparecen en el perfil público de una creadora,
 * solo visibles si el visitante autenticado es OTRA creadora (rol creator/model).
 *
 *   📡 Directo    → opt-in a recibir push cuando esa creadora se conecte en cam.
 *                  Solo se renderiza si la creadora target tiene al menos una
 *                  plataforma de webcam configurada (`target_has_cam: true`).
 *   🛍️ Servicios → opt-in a recibir push cuando publique un nuevo servicio.
 *                  Siempre visible.
 *
 * Cada botón llama a `PATCH /api/creator-follows/{id}/events { events:[...] }`,
 * que hace upsert (crea la fila si no existe) o delete (si vaciamos la lista).
 *
 * Props:
 *   creatorId  string — id de la creadora del perfil
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Radio, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
// ── Single button ──────────────────────────────────────────────────────────
function EventButton({ active, saving, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={saving}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-heading font-bold uppercase tracking-wide border-2 transition-all disabled:opacity-60 ${
        active
          ? "bg-neon-pink/20 border-neon-pink text-neon-pink hover:bg-neon-pink/30"
          : "bg-transparent border-border text-zinc-300 hover:border-neon-pink/60 hover:text-neon-pink"
      }`}
      title={active ? `Tocar para dejar de recibir avisos: ${label}` : `Recibir avisos: ${label}`}
    >
      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

export default function FollowCreatorButton({ creatorId }) {
  const { user } = useAuth();
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(null); // "live" | "services" | null
  const [allowed, setAllowed]   = useState(false);
  const [hasCam, setHasCam]     = useState(false);
  const [followLive, setFollowLive]         = useState(false);
  const [followServices, setFollowServices] = useState(false);

  useEffect(() => {
    if (!creatorId) return;
    // Skip for anonymous users — the endpoint requires auth and the buttons
    // are hidden for non-creator visitors anyway.
    if (!user) { setLoading(false); return; }
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/creator-follows/${creatorId}`, { withCredentials: true });
        if (cancel) return;
        setAllowed(!!res.data.allowed);
        setHasCam(!!res.data.target_has_cam);
        setFollowLive(!!res.data.following_live);
        setFollowServices(!!res.data.following_services);
      } catch {
        if (!cancel) setAllowed(false);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [creatorId]);

  const persist = async (which) => {
    if (saving) return;
    setSaving(which);
    // Compute next events array
    const nextLive     = which === "live"     ? !followLive     : followLive;
    const nextServices = which === "services" ? !followServices : followServices;
    const events = [];
    if (nextLive) events.push("live");
    if (nextServices) events.push("services");
    // Optimistic
    setFollowLive(nextLive);
    setFollowServices(nextServices);
    try {
      await axios.patch(
        `${API}/creator-follows/${creatorId}/events`,
        { events },
        { withCredentials: true }
      );
      // Friendly toast on the most recent action
      const wasOff = (which === "live" && !followLive) || (which === "services" && !followServices);
      const labelFor = which === "live" ? "directos" : "nuevos servicios";
      toast.success(wasOff ? `Te avisaremos de sus ${labelFor}` : `Has dejado de recibir avisos de ${labelFor}`);
    } catch (e) {
      // Rollback
      setFollowLive(followLive);
      setFollowServices(followServices);
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(null);
    }
  };

  if (loading || !allowed) return null;

  return (
    <div className="inline-flex items-center gap-2 flex-wrap" data-testid="follow-creator-buttons">
      {hasCam && (
        <EventButton
          active={followLive}
          saving={saving === "live"}
          onClick={() => persist("live")}
          icon={Radio}
          label="Directo"
          testId="follow-live-btn"
        />
      )}
      <EventButton
        active={followServices}
        saving={saving === "services"}
        onClick={() => persist("services")}
        icon={ShoppingBag}
        label="Servicios"
        testId="follow-services-btn"
      />
    </div>
  );
}
